import { useEffect, useState, useCallback } from 'react';
import {
  FolderArchive, Folder, FolderOpen, Search, Download, FileText, FileSpreadsheet,
  File as FileIcon, Trash2, RefreshCw, Settings as SettingsIcon, ChevronRight, ChevronDown,
  CheckCircle2, AlertTriangle, Cloud, PlayCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const SOURCE_LABEL = {
  entra: 'Microsoft Entra',
  aws: 'AWS Governance',
  employee_access: 'Employee Access',
  audit_logs: 'Audit Logs',
};

const RETENTION_OPTIONS = [
  { v: '1y', l: '1 Year' },
  { v: '3y', l: '3 Years' },
  { v: '5y', l: '5 Years' },
  { v: 'forever', l: 'Forever (default)' },
];

export default function ReportVault() {
  const [tab, setTab] = useState('browse');

  return (
    <div className="space-y-5" data-testid="report-vault-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">
          Security &amp; Governance · Report Vault
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1 inline-flex items-center gap-2">
          <FolderArchive className="w-7 h-7 text-[#3D4A8E]" /> Report Vault
        </h1>
        <p className="text-slate-500 mt-1">
          Compliance evidence archive. Every report generated by Security &amp; Governance modules is captured here automatically.
          Daily audit log exports run at 23:59 UTC.
        </p>
      </div>

      <div className="border-b border-slate-200 flex gap-1">
        {[
          { k: 'browse', l: 'Browse Reports', Icon: FolderOpen },
          { k: 'settings', l: 'Vault Settings', Icon: SettingsIcon },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            data-testid={`vault-tab-${t.k}`}
            className={`px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 border-b-2 -mb-px whitespace-nowrap ${
              tab === t.k ? 'border-[#3D4A8E] text-[#3D4A8E]' : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}>
            <t.Icon className="w-3.5 h-3.5" /> {t.l}
          </button>
        ))}
      </div>

      {tab === 'browse' && <BrowsePanel />}
      {tab === 'settings' && <SettingsPanel />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Browse panel — folder tree + file list with filters
// ---------------------------------------------------------------------------
function BrowsePanel() {
  const [tree, setTree] = useState([]);
  const [folder, setFolder] = useState(null);
  const [q, setQ] = useState('');
  const [sourceModule, setSourceModule] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadTree = useCallback(async () => {
    try { const { data } = await api.get('/vault/folders'); setTree(data || []); }
    catch (e) { toast.error('Failed to load folder tree'); }
  }, []);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (folder) params.set('folder', folder);
      if (sourceModule) params.set('source_module', sourceModule);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      params.set('limit', '500');
      const { data } = await api.get(`/vault/files?${params.toString()}`);
      setFiles(data || []);
    } catch (e) { toast.error('Failed to load files'); }
    finally { setLoading(false); }
  }, [q, folder, sourceModule, dateFrom, dateTo]);

  useEffect(() => { loadTree(); }, [loadTree]);
  useEffect(() => { const t = setTimeout(loadFiles, 250); return () => clearTimeout(t); }, [loadFiles]);

  const archive = async (id, name) => {
    if (!window.confirm(`Archive "${name}"? It will be hidden from the default view.`)) return;
    try { await api.delete(`/vault/files/${id}`); toast.success('Archived'); loadFiles(); loadTree(); }
    catch { toast.error('Archive failed'); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
      {/* Folder Tree */}
      <div className="bg-white border border-slate-200 rounded-2xl p-2 h-fit lg:sticky lg:top-4">
        <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">Folders</div>
        <button
          onClick={() => setFolder(null)}
          data-testid="vault-folder-all"
          className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-semibold ${
            folder === null ? 'bg-[#3D4A8E] text-white' : 'text-slate-700 hover:bg-slate-50'
          }`}>
          📂 All Reports
        </button>
        <div className="mt-1 space-y-0.5">
          <FolderTreeView tree={tree} folder={folder} setFolder={setFolder} />
        </div>
        <button onClick={() => { loadTree(); loadFiles(); }} data-testid="vault-refresh"
                className="w-full mt-2 text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center justify-center gap-1 py-1.5">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Files list with filters */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search file name / report type / generator…"
                   className="rounded-xl pl-9" data-testid="vault-search" />
          </div>
          <select value={sourceModule} onChange={e => setSourceModule(e.target.value)}
                  data-testid="vault-filter-source"
                  className="text-xs border border-slate-200 rounded-xl px-3 py-2 bg-white">
            <option value="">All sources</option>
            <option value="entra">Microsoft Entra</option>
            <option value="aws">AWS Governance</option>
            <option value="employee_access">Employee Access</option>
            <option value="audit_logs">Audit Logs</option>
          </select>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                 data-testid="vault-filter-from" className="rounded-xl w-36 text-xs" />
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                 data-testid="vault-filter-to" className="rounded-xl w-36 text-xs" />
        </div>

        {folder && (
          <div className="text-xs text-slate-500 inline-flex items-center gap-2 px-3 py-1 bg-slate-50 rounded-full">
            <Folder className="w-3 h-3" /> {folder}
            <button onClick={() => setFolder(null)} className="text-rose-500 ml-1">×</button>
          </div>
        )}

        {/* List */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm" data-testid="vault-file-table">
            <thead className="bg-white text-slate-500 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">File</th>
                <th className="text-left px-3 py-2">Folder</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">Generated</th>
                <th className="text-right px-3 py-2">Size</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400 text-xs">Loading…</td></tr>
              )}
              {!loading && files.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400 text-xs">
                  No reports match. Try clearing filters, or generate a report from Entra / AWS / Employee Access to populate.
                </td></tr>
              )}
              {!loading && files.map(f => (
                <tr key={f._id} className="border-t border-slate-200 bg-white hover:bg-slate-50"
                    data-testid={`vault-file-${f._id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-start gap-2">
                      <FileIconFor ext={f.extension} />
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-slate-900 truncate">{f.file_name}</div>
                        <div className="text-[10px] text-slate-500">{f.report_type}</div>
                        {f.onedrive_url && (
                          <div className="text-[10px] mt-0.5 text-emerald-700 inline-flex items-center gap-1">
                            <Cloud className="w-2.5 h-2.5" /> Synced to OneDrive
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-600">{f.folder_path}</td>
                  <td className="px-3 py-2 text-[11px]">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      f.source_module === 'entra' ? 'bg-indigo-50 text-indigo-700' :
                      f.source_module === 'aws' ? 'bg-amber-50 text-amber-700' :
                      f.source_module === 'employee_access' ? 'bg-emerald-50 text-emerald-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>{SOURCE_LABEL[f.source_module] || f.source_module}</span>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-600">
                    <div>{(f.generated_at || '').slice(0, 10)}</div>
                    <div className="text-[10px] text-slate-400">by {f.generated_by_name || '—'}</div>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-600 text-right">
                    {formatBytes(f.file_size)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <a href={`/api/vault/files/${f._id}/download?auth=${localStorage.getItem('hrms_token')}`}
                       data-testid={`vault-download-${f._id}`}
                       className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-[11px] font-semibold mr-1">
                      <Download className="w-3 h-3" /> Download
                    </a>
                    <button onClick={() => archive(f._id, f.file_name)}
                            data-testid={`vault-archive-${f._id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 text-slate-700 hover:bg-rose-50 hover:text-rose-700 text-[11px] font-semibold">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loading && <div className="text-[10px] text-slate-400 text-right">
          {files.length} file{files.length === 1 ? '' : 's'} shown
        </div>}
      </div>
    </div>
  );
}

function FolderTreeView({ tree, folder, setFolder }) {
  const [collapsed, setCollapsed] = useState({});
  const toggle = (path) => setCollapsed(c => ({ ...c, [path]: !c[path] }));

  // Flatten the tree into a list with depth — no recursive JSX.
  const items = [];
  for (const root of tree || []) {
    items.push({ ...root, depth: 0, hasChildren: (root.children || []).length > 0 });
    if (collapsed[root.path]) continue;
    for (const c1 of root.children || []) {
      items.push({ ...c1, depth: 1, hasChildren: (c1.children || []).length > 0 });
      if (collapsed[c1.path]) continue;
      for (const c2 of c1.children || []) {
        items.push({ ...c2, depth: 2, hasChildren: false });
      }
    }
  }

  return (
    <div className="space-y-0.5">
      {items.map(item => {
        const isActive = folder === item.path;
        const open = !collapsed[item.path];
        return (
          <div key={item.path} className="flex items-center gap-1" style={{ paddingLeft: item.depth * 8 }}>
            {item.hasChildren ? (
              <button onClick={() => toggle(item.path)} className="text-slate-400 hover:text-slate-700 w-4">
                {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              </button>
            ) : (
              <span className="w-4" />
            )}
            <button
              onClick={() => setFolder(item.path)}
              data-testid={`vault-folder-${item.path.replace(/[^a-zA-Z0-9]/g, '-')}`}
              className={
                'flex-1 text-left px-1.5 py-1 rounded-md text-[11px] font-medium inline-flex items-center gap-1.5 ' +
                (isActive ? 'bg-[#3D4A8E] text-white' : 'text-slate-700 hover:bg-slate-50')
              }>
              {item.hasChildren && open ? <FolderOpen className="w-3 h-3" /> : <Folder className="w-3 h-3" />}
              {item.name}
              {item.count > 0 && (
                <span className={
                  'ml-auto text-[9px] px-1.5 py-0.5 rounded-full ' +
                  (isActive ? 'bg-white/20' : 'bg-slate-100 text-slate-500')
                }>{item.count}</span>
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function FileIconFor({ ext }) {
  if (ext === 'pdf') return <FileText className="w-4 h-4 text-rose-600 mt-0.5" />;
  if (ext === 'xlsx') return <FileSpreadsheet className="w-4 h-4 text-emerald-600 mt-0.5" />;
  return <FileIcon className="w-4 h-4 text-slate-500 mt-0.5" />;
}

function formatBytes(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Settings panel — OneDrive integration + retention
// ---------------------------------------------------------------------------
function SettingsPanel() {
  const [s, setS] = useState({
    onedrive_enabled: false, tenant_id: '', client_id: '', client_secret: '',
    drive_type: 'user', target_user_upn: '', target_site_id: '',
    base_folder_path: '/HRMS/Reports', retention_policy: 'forever',
  });
  const [info, setInfo] = useState({ client_secret_set: false, last_test_at: null, last_test_status: null });
  const [busy, setBusy] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/vault/settings');
        setInfo(data);
        setS(prev => ({ ...prev, ...data, client_secret: '' }));
      } catch (e) { toast.error('Failed to load settings'); }
    })();
  }, []);

  const save = async () => {
    setBusy('save');
    try {
      const payload = {
        onedrive_enabled: s.onedrive_enabled,
        tenant_id: s.tenant_id, client_id: s.client_id,
        drive_type: s.drive_type, target_user_upn: s.target_user_upn,
        target_site_id: s.target_site_id, base_folder_path: s.base_folder_path,
        retention_policy: s.retention_policy,
      };
      if (s.client_secret) payload.client_secret = s.client_secret;
      await api.put('/vault/settings', payload);
      toast.success('Vault settings saved');
      setS(prev => ({ ...prev, client_secret: '' }));
      setInfo(i => ({ ...i, client_secret_set: s.client_secret ? true : i.client_secret_set }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(''); }
  };

  const testConnection = async () => {
    setBusy('test');
    try {
      const { data } = await api.post('/vault/onedrive/test');
      toast.success(`OneDrive connected ✓ — tenant root: ${data.tenant_root_site || 'unknown'}`);
      setInfo(i => ({ ...i, last_test_at: new Date().toISOString(), last_test_status: 'ok' }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Connection test failed');
      setInfo(i => ({ ...i, last_test_at: new Date().toISOString(), last_test_status: 'failed' }));
    } finally { setBusy(''); }
  };

  const runAuditExport = async () => {
    setBusy('export');
    try {
      await api.post('/vault/audit-export/run-now');
      toast.success('Audit log export queued — check the Audit Logs folder.');
    } catch (e) { toast.error('Audit export failed'); }
    finally { setBusy(''); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Left: Retention + Audit export */}
      <div className="space-y-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="vault-retention-card">
          <div>
            <h3 className="font-heading text-lg font-bold">Retention Policy</h3>
            <p className="text-xs text-slate-500 mt-1">Files past their retention window are soft-archived (hidden from default view), never hard-deleted. Default: <b>Forever</b>.</p>
          </div>
          <select value={s.retention_policy} onChange={e => setS({ ...s, retention_policy: e.target.value })}
                  data-testid="vault-retention-select"
                  className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white">
            {RETENTION_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
          <div className="text-xs text-slate-500">Current: <b>{RETENTION_OPTIONS.find(o => o.v === s.retention_policy)?.l || '—'}</b></div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="vault-audit-card">
          <div>
            <h3 className="font-heading text-lg font-bold">Daily Audit Log Export</h3>
            <p className="text-xs text-slate-500 mt-1">Automatically runs at <b>23:59 UTC</b> daily. The CSV lands in <code>Audit Logs/YYYY/Month/</code>.</p>
          </div>
          <Button onClick={runAuditExport} disabled={busy === 'export'}
                  data-testid="vault-audit-run-now"
                  className="rounded-full bg-[#3D4A8E] hover:bg-[#2e3a72]">
            <PlayCircle className="w-4 h-4 mr-1" />
            {busy === 'export' ? 'Running…' : 'Run audit export now'}
          </Button>
        </div>
      </div>

      {/* Right: OneDrive integration */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="vault-onedrive-card">
        <div>
          <h3 className="font-heading text-lg font-bold inline-flex items-center gap-2">
            <Cloud className="w-5 h-5 text-[#3D4A8E]" /> OneDrive / SharePoint Mirror
          </h3>
          <p className="text-xs text-slate-500 mt-1">Optional. When enabled, every vaulted report is also uploaded to your Microsoft 365 OneDrive or SharePoint document library via Microsoft Graph (app-only auth). Local copy is always retained.</p>
        </div>

        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={s.onedrive_enabled}
                 onChange={e => setS({ ...s, onedrive_enabled: e.target.checked })}
                 data-testid="vault-onedrive-enable" />
          Enable OneDrive sync
        </label>

        <div className="grid grid-cols-1 gap-2">
          <Field label="Tenant ID" testid="vault-tenant" value={s.tenant_id}
                 onChange={v => setS({ ...s, tenant_id: v })} />
          <Field label="Client ID" testid="vault-client-id" value={s.client_id}
                 onChange={v => setS({ ...s, client_id: v })} />
          <Field label={`Client Secret ${info.client_secret_set ? '(stored — leave blank to keep)' : ''}`}
                 testid="vault-client-secret" type="password" value={s.client_secret}
                 onChange={v => setS({ ...s, client_secret: v })} />

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-semibold text-slate-600">Drive Type</label>
              <select value={s.drive_type} onChange={e => setS({ ...s, drive_type: e.target.value })}
                      data-testid="vault-drive-type"
                      className="w-full text-sm border border-slate-200 rounded-xl px-2 py-2 bg-white mt-1">
                <option value="user">OneDrive (user)</option>
                <option value="site">SharePoint Site</option>
              </select>
            </div>
            <Field label={s.drive_type === 'user' ? 'Target User UPN' : 'Target Site ID'}
                   testid={s.drive_type === 'user' ? 'vault-target-upn' : 'vault-target-site'}
                   value={s.drive_type === 'user' ? s.target_user_upn : s.target_site_id}
                   onChange={v => setS(s.drive_type === 'user'
                     ? { ...s, target_user_upn: v } : { ...s, target_site_id: v })}
                   placeholder={s.drive_type === 'user' ? 'admin@contoso.onmicrosoft.com' : 'contoso.sharepoint.com,...'} />
          </div>

          <Field label="OneDrive Folder Path" testid="vault-folder-path" value={s.base_folder_path}
                 onChange={v => setS({ ...s, base_folder_path: v })}
                 placeholder="/HRMS/Reports" />
        </div>

        <div className="flex gap-2 pt-2 flex-wrap">
          <Button onClick={save} disabled={busy === 'save'}
                  data-testid="vault-save"
                  className="rounded-full bg-[#3D4A8E] hover:bg-[#2e3a72]">
            {busy === 'save' ? 'Saving…' : 'Save'}
          </Button>
          <Button onClick={testConnection} variant="outline" disabled={busy === 'test'}
                  data-testid="vault-test"
                  className="rounded-full">
            {busy === 'test' ? 'Testing…' : 'Test Connection'}
          </Button>
        </div>

        {info.last_test_at && (
          <div className="text-[11px] text-slate-500 pt-2 border-t border-slate-100 inline-flex items-center gap-2">
            {info.last_test_status === 'ok'
              ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              : <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />}
            Last test: {new Date(info.last_test_at).toLocaleString()} · <b>{info.last_test_status}</b>
          </div>
        )}

        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-[11px] text-indigo-900 space-y-1">
          <div className="font-bold">Azure App Registration setup (one-time):</div>
          <ol className="list-decimal list-inside space-y-0.5">
            <li>Sign in to <a href="https://portal.azure.com" target="_blank" rel="noreferrer" className="underline">Azure Portal</a> as a Global Admin.</li>
            <li>Microsoft Entra ID → <b>App registrations</b> → <b>New registration</b> (name: &quot;Lanvaus HRMS OneDrive&quot;, supported accounts: this tenant only).</li>
            <li>Copy <b>Application (client) ID</b> + <b>Directory (tenant) ID</b> from the Overview page → paste above.</li>
            <li><b>Certificates &amp; secrets</b> → <b>New client secret</b> → copy the Value (only shown once) → paste above.</li>
            <li><b>API permissions</b> → <b>Add</b> → Microsoft Graph → <b>Application permissions</b> → add <code>Files.ReadWrite.All</code> (and <code>Sites.ReadWrite.All</code> if using SharePoint) → click <b>Grant admin consent</b>.</li>
            <li>Click <b>Save</b> here → <b>Test Connection</b>.</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = 'text', placeholder }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-slate-600">{label}</label>
      <Input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
             placeholder={placeholder} data-testid={testid}
             className="rounded-xl mt-1 text-sm" />
    </div>
  );
}
